// Admin chat — chat-primary 2-column shell (H.3.4a / H.5 AG-UI migration).
//
// Migrated from @ai-sdk/react to @rmint/shell's AG-UI useChat in Task 11.
// The shell hook speaks the same flat AG-UI SSE protocol as the admin-agent's
// /chat route (AG-UI events: RUN_STARTED / TEXT_MESSAGE_* / TOOL_CALL_* /
// RUN_FINISHED / RUN_ERROR). The admin-agent's /chat schema was updated to
// accept { content, convId, turnId, runId, sessionId } (flat AG-UI) in the
// same task.
//
// Differences vs web-investor Chat:
//   - useChat({ api: '/admin-agent/chat', body: { sessionId } })
//   - slash fast-path → appendCell (same shape as investor chip-click)
//   - AdminChatProvider receives resumeFrom instead of setMessages (H.5)
//   - adminWidgetRegistry injected via WidgetRegistryProvider

import { useState } from 'react';
import { useChat } from '@rmint/shell';
import {
  MarkdownRenderer,
  InputWithSlashMenu,
  parseSlash,
  WidgetRegistryProvider,
} from '@rmint/shell';
import { SidebarChips } from '../components/admin/SidebarChips';
import { useSlashRegistry } from '../lib/use-slash-registry';
import { AdminChatProvider } from '../lib/admin-chat-context';
import { adminWidgetRegistry } from '../lib/widget-registry';

export function AdminChat() {
  const registry = useSlashRegistry();
  const sessionId = useSessionId();
  const [inputValue, setInputValue] = useState('');

  const chat = useChat({ api: '/admin-agent/chat', body: { sessionId } });

  const submit = async (text: string) => {
    const parsed = parseSlash(text, registry);
    if (parsed.kind === 'slash') {
      // Fast-path: bypass the LLM by calling /admin-agent/command directly.
      // A gated slash returns the gate's confirmation directive, which we
      // render as a synthetic assistant message — same path as agent-emitted
      // widgets. (G.2.4 verbatim, minus outreach's `scope`.)
      try {
        const res = await fetch('/admin-agent/command', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name: parsed.command, args: parsed.args, sessionId }),
        });
        const data = (await res.json()) as
          | { ok: true; name: string; result?: { widget?: string; error?: string } }
          | {
              ok: false;
              name?: string;
              gated?: boolean;
              decision?: { action: string; directive?: string; reason?: string };
              error?: string;
            };

        let assistantText: string;
        if (!data.ok && data.gated && data.decision) {
          assistantText =
            data.decision.action === 'confirm' && data.decision.directive
              ? data.decision.directive
              : `:::callout[danger]{title="Action blocked"}\n${data.decision.reason ?? 'blocked'}\n:::`;
        } else if (data.ok && typeof data.result?.widget === 'string') {
          assistantText = data.result.widget;
        } else {
          assistantText = `_${(!data.ok && data.error) || (data.ok && data.result?.error) || 'no result'}_`;
        }

        chat.appendCell(text, assistantText);
      } catch {
        // Fall through to the chat path so the founder sees something — the
        // proxy may be offline or the agent may not have the tool.
        void chat.send(text);
      }
      return;
    }
    void chat.send(text);
  };

  const onChipSubmit = (slash: string) => submit(slash);
  const onChipPopulate = (slash: string) => setInputValue(slash);

  return (
    <AdminChatProvider resumeFrom={chat.resumeFrom}>
      <WidgetRegistryProvider value={adminWidgetRegistry}>
        <div className="grid grid-cols-[16rem_1fr] gap-0 -mx-6 -my-6 min-h-[calc(100vh-3.5rem)]">
          <aside className="border-r border-border bg-card/40 px-4 py-6 overflow-y-auto">
            <SidebarChips onChipSubmit={onChipSubmit} onChipPopulate={onChipPopulate} />
          </aside>
          <main className="flex flex-col h-[calc(100vh-3.5rem)]">
            <header className="border-b border-border px-4 py-3 flex items-center justify-between">
              <h1 className="text-base font-semibold">Admin</h1>
            </header>
            <div data-testid="chat-stream" className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
              {chat.messages.map((m) => (
                <div
                  key={m.id}
                  data-message-id={m.id}
                  className={m.role === 'user' ? 'text-right' : 'text-left'}
                >
                  {m.role === 'user' ? (
                    <span className="inline-block rounded-lg bg-card px-3 py-2 text-sm">
                      {m.content}
                    </span>
                  ) : (
                    <div className="inline-block rounded-lg bg-card px-3 py-2 text-sm text-left">
                      <MarkdownRenderer isAnimating={chat.isStreaming}>
                        {m.content}
                      </MarkdownRenderer>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <InputWithSlashMenu
              value={inputValue}
              setValue={setInputValue}
              onSubmit={submit}
              disabled={chat.isStreaming}
              registry={registry}
              placeholder="Type / for commands, or ask the admin agent…"
            />
          </main>
        </div>
      </WidgetRegistryProvider>
    </AdminChatProvider>
  );
}

function useSessionId(): string {
  const [id] = useState(() => {
    const existing = sessionStorage.getItem('admin-session-id');
    if (existing) return existing;
    const fresh = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem('admin-session-id', fresh);
    return fresh;
  });
  return id;
}
